import { formatGbp } from './formatGbp';
import { getNHSBandPrice } from '../constants/nhsBandPrices';

/** @deprecated Use formatGbp from ./formatGbp — kept for re-exports */
export { formatGbp };

export const NHS_EXEMPT_COST_TEXT = 'NHS exempt · £0.00';

export function isNhsExemptConsent(consent) {
  return consent?.isExempt === true || consent?.patientFundingType === 'exempt';
}

/**
 * A lone total may be shown on the first row only when nothing is priced per row and the visit is private.
 * Otherwise reopening the draft reads it back as a row price and counts it twice.
 */
export function canPutTotalOnFirstRow(patientFundingType, sumOfRowCosts) {
  if (sumOfRowCosts > 0) return false;
  return !patientFundingType || patientFundingType === 'private';
}

export function hasPositiveCost(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0;
}

export function nhsBandLabel(band) {
  return String(band) === '4' ? 'Band 4 (Urgent)' : `Band ${band}`;
}

/**
 * Split a saved mixed consent into its NHS band charge and private fees.
 * Returns null for anything that is not mixed NHS + private.
 */
export function mixedCostBreakdown(consent) {
  if (consent?.patientFundingType !== 'mixed') return null;
  const total = Number(consent?.estimatedCost ?? consent?.treatmentCost) || 0;
  const band = consent?.nhsBand ? String(consent.nhsBand) : '';
  const nhsExempt = consent?.nhsExempt === true;
  let nhsCost = 0;
  if (!nhsExempt) {
    nhsCost = hasPositiveCost(consent?.nhsBandCost) ? Number(consent.nhsBandCost) : getNHSBandPrice(band);
  }
  const privateCost = hasPositiveCost(consent?.privateCost)
    ? Number(consent.privateCost)
    : Math.max(total - nhsCost, 0);
  return { band, nhsExempt, nhsCost, privateCost, total };
}

/** True when a saved treatment row carries its own private price. */
export function treatmentRowHasPrivatePrice(row) {
  return hasPositiveCost(row?.customPrice ?? row?.estimatedCost);
}

function treatmentLabel(t) {
  const parts = [t.tooth, t.treatment || t.diagnosis].filter(Boolean);
  return parts.join(' — ') || 'Treatment';
}

/**
 * Build patient-facing HTML for the Cost section from funding type and treatment rows.
 */
export function buildCostSectionHtml({ funding, treatments, totalCost }) {
  const total = formatGbp(totalCost ?? 0);
  const rows = Array.isArray(treatments) ? treatments.filter((t) => t.diagnosis || t.treatment) : [];

  if (!funding?.patientFundingType) {
    return `<p>The total cost for your treatments is <strong>${total}</strong>.</p>`;
  }

  if (funding.patientFundingType === 'exempt') {
    return `<p><strong>NHS exempt:</strong> You are exempt from NHS dental charges for this treatment. There is no charge to you.</p><p><strong>Total:</strong> ${formatGbp(0)}</p>`;
  }

  if (funding.patientFundingType === 'nhs') {
    const band = funding.nhsBand;
    const bandLabel = band === '4' ? 'Band 4 (Urgent)' : `Band ${band}`;
    return `<p><strong>NHS treatment:</strong> Your treatment is charged under the NHS <strong>${bandLabel}</strong> charge of <strong>${formatGbp(funding.nhsBandCost)}</strong>. This single NHS band fee covers all NHS treatment included in this plan for this course of treatment.</p><p><strong>Total:</strong> ${formatGbp(funding.nhsBandCost ?? totalCost)}</p>`;
  }

  if (funding.patientFundingType === 'private') {
    const items = rows
      .map((t) => {
        const cost = typeof t.cost === 'number' && !isNaN(t.cost) ? t.cost : 0;
        return `<li><strong>${treatmentLabel(t)}:</strong> ${formatGbp(cost)}</li>`;
      })
      .join('');
    return `<p><strong>Private treatment:</strong> The following private fees apply:</p><ul>${items}</ul><p><strong>Total:</strong> ${total}</p>`;
  }

  if (funding.patientFundingType === 'mixed') {
    const nhsItems = rows.filter((t) => !(typeof t.cost === 'number' && t.cost > 0));
    const privateItems = rows.filter((t) => typeof t.cost === 'number' && t.cost > 0);
    const band = funding.nhsBand;
    const bandLabel = band === '4' ? 'Band 4 (Urgent)' : `Band ${band}`;

    let html = `<p><strong>Mixed NHS and private treatment:</strong> Your treatment plan includes both NHS and private elements. Your charges are broken down below.</p>`;
    if (funding.nhsExempt) {
      html += `<p><strong>NHS exempt:</strong> ${formatGbp(0)} — you are exempt from NHS dental charges, so there is no charge for the NHS treatment${nhsItems.length > 0 ? ':' : '.'}</p>`;
      if (nhsItems.length > 0) {
        html += `<ul>${nhsItems.map((t) => `<li>${treatmentLabel(t)}</li>`).join('')}</ul>`;
      }
    } else {
      html += `<p><strong>NHS (${bandLabel}):</strong> ${formatGbp(funding.nhsBandCost)} — one NHS band charge covering:</p>`;
      if (nhsItems.length > 0) {
        html += `<ul>${nhsItems.map((t) => `<li>${treatmentLabel(t)}</li>`).join('')}</ul>`;
      } else {
        html += `<p><em>NHS band charge applies to NHS elements of this plan.</em></p>`;
      }
    }
    if (privateItems.length > 0) {
      html += `<p><strong>Private fees:</strong></p><ul>${privateItems
        .map((t) => `<li><strong>${treatmentLabel(t)}:</strong> ${formatGbp(t.cost)}</li>`)
        .join('')}</ul>`;
      html += `<p><strong>Private subtotal:</strong> ${formatGbp(funding.privateCost)}</p>`;
    }
    html += funding.nhsExempt
      ? `<p><strong>Total (private only):</strong> ${total}</p>`
      : `<p><strong>Total (NHS band + private):</strong> ${total}</p>`;
    return html;
  }

  return `<p><strong>Total:</strong> ${total}</p>`;
}

/** Replace or append the Cost section in AI-generated sections. */
export function applyCostSectionToGeneratedSections(sections, costContext) {
  const html = buildCostSectionHtml(costContext);
  const list = Array.isArray(sections) ? [...sections] : [];
  const idx = list.findIndex((s) => (s.title || '').trim().toLowerCase() === 'cost');
  if (idx >= 0) {
    list[idx] = { ...list[idx], content: html };
    return list;
  }
  return [...list, { title: 'Cost', content: html }];
}
